import { isEqual } from 'lodash';
import { useRef } from 'react';
import { StagedContext } from '@/vantage/types/Context.types';
import { t } from 'i18next';
import { MAX_POSTGRES_EPOCH_MILLISEC, VantageNames } from '@/vantage/constants';
import { setActiveExtensions } from '@/vantage/vantageWorkbook.actions';
import { sqVantageContextStore, sqVantageWorkbookStore } from '@/core/core.stores';
import type { ExtensionSchema } from '@/vantage/types/Extension.types';
import { safeGetJsonProperty } from '@/utilities/httpHelpers.utilities';

export type SeeqUser = {
  email: string;
  firstName: string;
  id: string;
  isActive: boolean;
  isAdmin: boolean;
  isArchived: boolean;
  isEnabled: boolean;
  isPasswordSettable: boolean;
  isRedacted: boolean;
  lastLoginAt: string;
  lastName: string;
  name: string;
  type: string;
  username: string;
  groups: any[];
};

export function snakeToCamel(obj: any): any {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(snakeToCamel);
  }

  return Object.entries(obj).reduce((acc, [key, value]) => {
    const camelKey = key.replace(/_\w/g, function (match) {
      return match[1].toUpperCase();
    });

    return {
      ...acc,
      [camelKey]: snakeToCamel(value),
    };
  }, {});
}

export function camelToSnake(camelCase: string): string {
  return camelCase
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2')
    .toLowerCase();
}

export function mergeData<T>(newData: T[], existingData: T[], getKey: (row: T) => string): T[] {
  const existingMap = new Map<string, any>(existingData?.map((r) => [getKey(r), r]));
  newData.forEach((r) => existingMap.set(getKey(r), r));
  return Array.from(existingMap.values()).filter((capsule) => !capsule[VantageNames.Columns.DeletedAt]);
}

export function useDeepCompareWithRef<T>(value: T) {
  const ref = useRef<T>(undefined);
  if (!isEqual(value, ref.current)) {
    ref.current = value; //ref.current contains the previous object value
  }

  return ref.current;
}

export const serializeNote = (stagedContext: StagedContext[]) => {
  const userContext =
    stagedContext.find((context) => context.identifier === VantageNames.Extensions.Notes)?.content?.value || '';
  const otherExtensions = stagedContext.filter((context) => context.identifier !== VantageNames.Extensions.Notes);

  const serializedNote = {
    identifier: VantageNames.Extensions.Notes,
    content: { value: userContext },
    extensionContexts: [...otherExtensions],
  };
  return [...otherExtensions, serializedNote];
};

export function parseAsCamel(s: string) {
  return s ? snakeToCamel(JSON.parse(s)) : undefined;
}

type CoercableToDate = number | string | Date;

export function ensureDate(x: CoercableToDate | null): Date | undefined {
  if (x) {
    return typeof x === 'number' ? new Date(x / 1e6) : new Date(x);
  }
}

export function ensureIso(x: CoercableToDate | null): string | undefined {
  return ensureDate(x)?.toISOString();
}

export function isMaxEnd(x: CoercableToDate | null): boolean {
  return ensureDate(x)?.valueOf() === MAX_POSTGRES_EPOCH_MILLISEC;
}

export const getUniqueColor = (usedColors: Set<string>) => {
  let color;
  do {
    color = `#${Math.floor(Math.random() * 0xffffff)
      .toString(16)
      .padStart(6, '0')}`;
  } while (usedColors.has(color));
  return color;
};

export const createUniqueColors = (
  items: { color: string | undefined; id: string }[],
  usedColors: Set<string>,
  conditionColorMap: Map<string, string>,
) => {
  items.forEach((item) => {
    if (item.color && !usedColors.has(item.color)) {
      usedColors.add(item.color);
      conditionColorMap.set(item.id, item.color);
    } else if (!conditionColorMap.has(item.id)) {
      const newColor = getUniqueColor(usedColors);
      item.color = newColor;
      usedColors.add(newColor);
      conditionColorMap.set(item.id, newColor);
    } else {
      item.color = conditionColorMap.get(item.id);
    }
  });
};

/**
 * Finds the extreme value (minimum or maximum) in an array of values after applying a transformation function.
 *
 * Use in place of `Math.min(...values)` or `Math.max(...values)` for large arrays.
 *
 * @param values - The array of values to search through.
 * @param extreme - Specifies whether to find the minimum or maximum value.
 * @param {(value: T) => number} [valueTransform=(value) => value as number] - A function to transform each value in
 *   the array to a number for comparison.
 */
export function optimizedExtreme<T>(
  values: T[],
  extreme: 'min' | 'max',
  valueTransform: (value: T) => number = (value) => value as number,
): number {
  let extremeValue = extreme === 'max' ? -Infinity : Infinity;

  for (const value of values) {
    const dateValue = valueTransform(value);
    if (extreme === 'max') {
      if (dateValue > extremeValue) extremeValue = dateValue;
    } else {
      if (dateValue < extremeValue) extremeValue = dateValue;
    }
  }

  return extremeValue;
}

export function isMultipleValues(value: string): boolean {
  return value === t('VANTAGE.EVIDENCE_TABLE.MULTIPLE_VALUES');
}

interface TranslatableLabel {
  label?: string;
  labelTranslationKey?: string;
}

export function translateLabelIfPossible(item: TranslatableLabel | undefined): string {
  return (item?.labelTranslationKey ? t(item.labelTranslationKey) : item?.label) ?? '*missing label*';
}

// TODO : CRAB-46379 : Remove the hardcoded pinning of reason codes once pinning works in the UI
export function collapseNonPinnedExtensionUi(): void {
  setActiveExtensions(
    sqVantageWorkbookStore.activeExtensions.filter(
      (ext) => sqVantageContextStore.pinnedExtensions.includes(ext) || ext === VantageNames.Extensions.ReasonCodes,
    ),
  );
}

export const getExtensions = (id: string): Promise<ExtensionSchema[]> => safeGetJsonProperty(id, 'extensions', []);

/**
 * Searches an array and replaces the first item matching the given predicate;
 * if none is found, appends the item to the array.
 *
 * @param items - array of items
 * @param item - item to replace existing item with or append to array
 * @param finder - predicate to find the item to replace
 */
export const replaceOrAppend = <T>(items: T[], item: T, finder: (arg: T) => boolean): T[] => {
  const index = items.findIndex(finder);
  const itemsCopy = [...items];
  if (index !== -1) {
    itemsCopy[index] = item;
  } else {
    itemsCopy.push(item);
  }
  return itemsCopy;
};
